/**
 * Quote send — compose + channel logic (MOCKUP_5 · 2026-06-16).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE SEND MODEL (owner-approved MOCKUP_5):
 *   • The WhatsApp message is the HERO — a warm, readable note with the
 *     service + agreed price + "reply YES" call to action.
 *   • Email goes alongside (a safety net; works regardless of WhatsApp).
 *   • PDF is demoted to an optional "share" affordance.
 *
 *  CHANNEL REALITY (LOCKED):
 *   • EMAIL works NOW (Brevo, via the deployed `sendQuoteViaAiSensy` callable's
 *     Brevo branch — same Brevo infra the client-portal-link work uses).
 *   • WHATSAPP is GATED behind the owner's BSP template approval. The deployed
 *     send callable already self-gates on `quoteTemplates/{campaign}.
 *     aisensyApproved === false`; this module ALSO surfaces the gate up front
 *     (`whatsappGated`) so the UI disables the WA channel with a clear note
 *     instead of firing a doomed send. (Per `project_waba_ownership_cloud_api_
 *     migration`, AiSensy is being retired in favour of Cloud-API direct — the
 *     deployed AiSensy callable is the transitional reality; flagged to Varun.)
 *
 *  PURE — no Firebase, no React. The .tsx owns the httpsCallable round trip;
 *  this composes the message + decides channels + interprets the outcome.
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { Currency } from './firms';
import { quoteMoney, quotePayNow, payNowReason, type Quote, type QuoteMilestone } from './quoteModel';

/* ════════════════════ Greeting name (shared, PURE) ════════════════════ */

/**
 * PURE — the warm greeting name for a full name, gender-neutral:
 *   • if the first whitespace token is an INITIAL (≤ 2 chars, e.g. "P", "PK",
 *     "P.") → use the FULL name ("P K Agrawal") so we never greet a bare initial.
 *   • otherwise → the first name ("Rajesh Sharma" → "Rajesh").
 * Empty / blank input → '' (callers apply their own fallback). The greeting
 * text around it stays "Namaste {name} ji".
 */
export function greetingName(fullName: string | null | undefined): string {
  const full = (fullName || '').trim();
  if (!full) return '';
  const tokens = full.split(/\s+/);
  const firstIsInitial = tokens[0].replace(/\./g, '').length <= 2;
  return firstIsInitial ? full : tokens[0];
}

/* ════════════════════ Static firm payment details (Bug D) ════════════════════
 *
 * Owner chose STATIC UPI + QR + bank details over Razorpay for now. These slots
 * are read from `firms/{firmId}` (upiId, bankAccName, bankAccNo, bankIfsc,
 * bankName, qrUrl). The preview renders whatever is present and degrades
 * gracefully when a value is missing. The CF renders the same block in the
 * email — preview == reality (Bug E). */
export interface FirmPayment {
  upiId?: string | null;
  bankAccName?: string | null;
  bankAccNo?: string | null;
  bankIfsc?: string | null;
  bankName?: string | null;
  qrUrl?: string | null;
  /** SWIFT/BIC — shown on USD (foreign-wire) quotes only. */
  swift?: string | null;
  /** AD/BSR code for foreign inward remittance — USD quotes only. */
  bsrAdCode?: string | null;
}

/** PURE — true when the firm carries enough to take a payment statically. */
export function hasStaticPayment(fp: FirmPayment | null | undefined): boolean {
  return !!(fp && (fp.upiId || fp.qrUrl || (fp.bankAccNo && fp.bankIfsc)));
}

/**
 * PURE — should the UPI QR render for this quote?
 * UPI is an India-only rail; a foreign (USD) payer cannot scan a UPI QR, so the
 * QR is suppressed on USD quotes even when a `qrUrl` is on file.
 */
export function qrVisible(
  fp: FirmPayment | null | undefined,
  currency?: Currency,
): boolean {
  return !!(fp && fp.qrUrl) && currency !== 'USD';
}

/**
 * PURE — the plain-text "How to pay" lines (mirrors the CF email block).
 *
 * Currency-conditional (UPI is India-only):
 *   • INR → UPI + bank details (+ a QR alongside).
 *   • USD → bank A/c + SWIFT + AD/BSR code; UPI and QR are OMITTED (a foreign
 *     payer wires to the bank — they cannot use UPI).
 */
export function staticPaymentLines(
  fp: FirmPayment | null | undefined,
  currency?: Currency,
): string[] {
  const out: string[] = [];
  if (!fp) return out;
  const isUsd = currency === 'USD';
  if (!isUsd && fp.upiId) out.push(`UPI: ${fp.upiId}`);
  if (fp.bankAccName) out.push(`A/c name: ${fp.bankAccName}`);
  if (fp.bankName) out.push(`Bank: ${fp.bankName}`);
  if (fp.bankAccNo) out.push(`A/c no: ${fp.bankAccNo}`);
  if (fp.bankIfsc) out.push(`IFSC: ${fp.bankIfsc}`);
  if (isUsd) {
    if (fp.swift) out.push(`SWIFT: ${fp.swift}`);
    if (fp.bsrAdCode) out.push(`AD code: ${fp.bsrAdCode}`);
  }
  return out;
}

/* ════════════════════ The message (MOCKUP_5 hero copy) ════════════════════ */

export interface SendSubject {
  clientName: string;
  serviceName: string;
  /** Agreed grand total the client pays. */
  total: number;
  currency: Currency;
  /** Firm payment details to render the "How to pay" block (Bug D/E). */
  firmPayment?: FirmPayment | null;
  /** Whether a QR image is on file (the text note points to it). */
  hasQr?: boolean;
}

/**
 * PURE — describe the ACTUAL line item(s) of a quote, not the tier (Bug B).
 * First line's desc; if multiple, append "+N more". Falls back to serviceName
 * only when no line desc exists.
 */
export function quoteServiceText(quote: Pick<Quote, 'lines' | 'serviceName' | 'tier'>): string {
  const descs = (quote.lines ?? [])
    .map((l) => (l.desc || l.svcId || '').trim())
    .filter(Boolean);
  if (descs.length === 0) return (quote.serviceName || quote.tier || 'Vastu Consultancy').trim();
  if (descs.length === 1) return descs[0];
  return `${descs[0]} +${descs.length - 1} more`;
}

/**
 * PURE — the WhatsApp / message body the client receives (MOCKUP_5 bubble).
 * A warm note: greeting ("ji" honorific) → service (the actual line item) →
 * price → payment-is-acceptance framing + the static "How to pay" details.
 * First name only, matching the mockup's "Namaste {First} ji 🙏".
 */
export function composeWhatsAppMessage(s: SendSubject): string {
  const first = greetingName(s.clientName) || 'there';
  const payLines = staticPaymentLines(s.firmPayment, s.currency);
  // UPI/QR is India-only — never point a USD payer at a QR.
  const showQr = !!s.hasQr && s.currency !== 'USD';
  const lines: string[] = [
    `Namaste ${first} ji 🙏`,
    '',
    `Here's your quote from Aayatan Veda:`,
    `• Service: ${s.serviceName}`,
    `• Your price: ${quoteMoney(s.total, s.currency)}`,
    '',
    `Your payment is your acceptance of this quotation.`,
  ];
  if (payLines.length > 0 || showQr) {
    lines.push('', 'How to pay:');
    for (const pl of payLines) lines.push(`• ${pl}`);
    if (showQr) lines.push('• Scan the QR we have sent to pay via any UPI app');
  }
  lines.push('', `Any questions? Reply right here anytime — we're happy to help. 🌿`);
  return lines.join('\n');
}

/** PURE — the email subject line. */
export function composeEmailSubject(s: SendSubject): string {
  return `Your quote from Aayatan Veda — ${s.serviceName}`;
}

/* ════════════════════ Unified quote message (av_quote_v6 mirror) ════════════════════
 *
 *  The SEND-SCREEN preview must read IDENTICALLY to what the backend actually
 *  sends (functions/avbp_quote_send.js `buildUnifiedQuoteParams`). These two
 *  helpers mirror the backend `formatMoney` + `fmtCreditLine` byte-for-byte so
 *  the money + credit lines match. The template is av_quote_v6 (adds a {{7}}
 *  pay link over v4/v5).
 */

/** PURE — money formatter mirroring avbp_quote_send.js:221 (USD → "US$500"). */
export function formatMoney(amount: number, currency: Currency): string {
  const n = Math.round(Number(amount) || 0);
  if (String(currency || '').toUpperCase() === 'USD') {
    return `US$${n.toLocaleString('en-US')}`;
  }
  return `₹${n.toLocaleString('en-IN')}`;
}

/**
 * PURE — a credit/adjustment display line, mirroring the backend `fmtCreditLine`:
 * a positive amount shows as a subtraction ("− US$350"); a zero/absent amount
 * shows the promotional teaser (so the line is never empty).
 */
function creditLine(amount: number | null | undefined, currency: Currency, teaser: string): string {
  const n = Number(amount) || 0;
  return n > 0 ? `− ${formatMoney(n, currency)}` : teaser;
}

/**
 * PURE — the {{7}} "You pay now" composed string: the amount due now PLAIN,
 * followed by the reason in brackets, e.g. "₹25,000 (for advance)" or
 * "US$500 (in full)". Mirrors `formatMoney` so the portal preview and the
 * backend send read byte-identically. Never blank (Meta rejects empty vars).
 */
export function payNowComposed(
  milestones: QuoteMilestone[] | null | undefined,
  grandTotal: number,
  currency: Currency,
): string {
  const amt = quotePayNow(milestones ?? [], grandTotal);
  const reason = payNowReason(milestones ?? []);
  return `${formatMoney(amt, currency)} (${reason})`;
}

export interface QuoteMessagePreview {
  /** Recipient display name (first name is derived from it). */
  clientName: string;
  /** The composed service line ({{2}} — quote.serviceLine). */
  serviceLine: string;
  /** {{3}} service value = the subtotal. */
  subtotal: number;
  /** {{4}} referral / relationship credit amount (0 → teaser). */
  referralCredit: number;
  /** {{5}} returning-client adjustment amount (0 → teaser). */
  priorServiceAdjustment: number;
  /** {{6}} the price the client pays = grand total. */
  grandTotal: number;
  currency: Currency;
  /**
   * {{7}} the "You pay now" composed string (amount + bracketed reason), e.g.
   * "₹25,000 (for advance)" / "US$500 (in full)". Build it with `payNowComposed`.
   * ALWAYS present — the you-pay-now LINE is universal (single or staged).
   */
  payNowComposed: string;
  /** {{8}} secure pay link — omit the line entirely when absent (never faked). */
  paymentLinkUrl?: string | null;
}

/**
 * PURE — the exact message the client receives (av_quote_v6). Mirrors the
 * backend param order + `formatMoney`/`fmtCreditLine`. The pay-link line is
 * included ONLY when a real link is present (the portal shows a muted
 * "added when the quote is sent" note in its place otherwise). No inline
 * bank/UPI block and no start-of-work clause — payment is a link + the PDF.
 */
export function composeQuoteMessagePreview(s: QuoteMessagePreview): string {
  const first = greetingName(s.clientName) || 'Friend';
  const link = (s.paymentLinkUrl || '').trim();
  const lines: string[] = [
    `Namaste ${first} ji 🙏`,
    '',
    `Thank you for the trust you've placed in Aayatan Veda. Here's your quotation for ${s.serviceLine}.`,
    '',
    `Service value: ${formatMoney(s.subtotal, s.currency)}`,
    `Referral / relationship credit: ${creditLine(s.referralCredit, s.currency, 'Earn this when you refer us 🌿')}`,
    `Returning-client adjustment: ${creditLine(s.priorServiceAdjustment, s.currency, 'Applies on your next project with us')}`,
    `Your price: ${formatMoney(s.grandTotal, s.currency)}`,
    '',
    // v7 {{7}} you-pay-now line — ALWAYS present (single-payment → "in full";
    // the full stage SCHEDULE lives in the PDF/email, not this bubble).
    `*You pay now* ${s.payNowComposed} — this confirms your quotation and begins the work. Any further stages and the full scope are in the attached PDF.`,
    '',
    // v7 {{8}} pay-link line — ALWAYS shown so the preview matches the sent
    // message structure. Before the quote is sent the live link isn't minted
    // yet, so a clear in-body placeholder stands in (never a faked URL).
    `To pay securely, tap here: ${link || '(a secure payment link is added when you send)'}`,
    '',
    'The attached PDF has the full scope and details. Your payment is your acceptance of this quotation.',
    '',
    `Any questions? Just reply here — we're happy to help. 🌿`,
  ];
  return lines.join('\n');
}

/* ════════════════════ Channels ════════════════════ */

export type SendChannel = 'whatsapp' | 'email';

export interface SendChannelState {
  channel: SendChannel;
  /** The destination address (phone / email) — null if none on file. */
  dest: string | null;
  /** Selected to send by default. */
  selected: boolean;
  /** Disabled (e.g. WhatsApp before BSP template approval, or no dest). */
  disabled: boolean;
  /** Short reason shown when disabled. */
  note: string;
}

/**
 * PURE — the two channel cards for the send screen (MOCKUP_5). Email is the
 * co-primary that works NOW; WhatsApp is selected ONLY when it has a dest AND
 * the BSP template is approved (`whatsappApproved`). Without approval the WA
 * card is disabled with the honest "needs a one-time Meta template approval"
 * note — email stays selected as the safety net.
 */
export function sendChannels(input: {
  email: string | null;
  phone: string | null;
  whatsappApproved: boolean;
}): SendChannelState[] {
  const email = (input.email || '').trim() || null;
  const phone = (input.phone || '').trim() || null;
  return [
    {
      channel: 'whatsapp',
      dest: phone,
      selected: !!phone && input.whatsappApproved,
      disabled: !phone || !input.whatsappApproved,
      note: !phone
        ? 'No WhatsApp number on file.'
        : !input.whatsappApproved
          ? 'Needs a one-time Meta template approval before WhatsApp quotes can send. Email works regardless.'
          : '',
    },
    {
      channel: 'email',
      dest: email,
      selected: !!email,
      disabled: !email,
      note: !email ? 'No email on file — add one to send by email.' : '',
    },
  ];
}

/** PURE — true when WhatsApp is gated (no approval) — drives the UI banner. */
export function whatsappGated(whatsappApproved: boolean): boolean {
  return !whatsappApproved;
}

/* ════════════════════ Outcome interpretation ════════════════════ */

/** The `sendQuoteViaAiSensy` callable response (channels it actually sent on). */
export interface SendQuoteResp {
  ok: boolean;
  sentVia?: string[];
  skipped?: string;
  error?: string;
}

export interface SendOutcome {
  ok: boolean;
  message: string;
  /** Channels actually delivered. */
  sentVia: SendChannel[];
}

/** PURE — interpret a send response into a toast-ready outcome. */
export function interpretSend(resp: SendQuoteResp): SendOutcome {
  const sentVia = (resp.sentVia ?? []).filter(
    (c): c is SendChannel => c === 'whatsapp' || c === 'email',
  );
  if (resp.skipped === 'already_sent') {
    return { ok: true, message: 'This quote was already sent.', sentVia };
  }
  if (resp.ok && sentVia.length > 0) {
    const human =
      sentVia.length === 2
        ? 'WhatsApp and email'
        : sentVia[0] === 'whatsapp'
          ? 'WhatsApp'
          : 'email';
    return { ok: true, message: `Quote sent by ${human}.`, sentVia };
  }
  if (resp.ok) {
    // The send succeeded but nothing delivered (e.g. WA gated + no email).
    return {
      ok: true,
      message:
        'Saved as sent, but no channel delivered — add an email, or approve the WhatsApp template, then resend.',
      sentVia,
    };
  }
  return {
    ok: false,
    message: resp.error
      ? `Could not send the quote (${resp.error}).`
      : 'Could not send the quote. Try again shortly.',
    sentVia,
  };
}
